import { Component } from "react";
import { useLocation } from "react-router-dom";

/**
 * The global error boundary (H-4).
 *
 * Before this existed, any exception thrown while rendering took the portal to a blank white screen —
 * on a customer's phone, with no message and no way back. `ErrorState` covers a FAILED REQUEST, which
 * react-query turns into data a page can render; it cannot help when the render itself throws.
 *
 * ── WHAT IS AND IS NOT CAUGHT (React's rule) ────────────────────────────────
 * CAUGHT: render, lifecycle and constructor exceptions in the subtree below — including a REJECTED
 * `React.lazy()` import, which is how a stale chunk after a deploy surfaces.
 * NOT CAUGHT: event handlers, timers and unawaited promise rejections. Every network call in this
 * portal goes through react-query, which captures rejections as `isError` for `ErrorState`, so that
 * gap is already covered and no global `unhandledrejection` hook is added here.
 *
 * ── TWO LEVELS ──────────────────────────────────────────────────────────────
 * `App.jsx` wraps the whole shell (last resort, and the only thing that can catch the header or the
 * auth provider failing). A second, route-level boundary wraps the page content inside the shell, so
 * a page crash leaves the header and bottom bar standing and the customer can tap elsewhere.
 *
 * ── RECOVERY ────────────────────────────────────────────────────────────────
 * `RouteErrorBoundary` is keyed by pathname: navigating away clears the error by remounting. "Try
 * again" re-renders for a transient failure. A chunk-load failure cannot be fixed by re-rendering, so
 * that case offers a reload instead.
 *
 * The session, the query cache and the current URL are untouched — only the failed subtree unmounts.
 */

function isChunkLoadError(error) {
  const message = String(error?.message || "");
  return (
    /failed to fetch dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    error?.name === "ChunkLoadError"
  );
}

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const chunk = isChunkLoadError(error);

    return (
      <div className="text-center text-muted py-5 px-3">
        <i className="bi bi-exclamation-octagon" style={{ fontSize: 32, opacity: 0.5 }} />
        <h1 className="h6 mt-3 text-body">
          {chunk ? "This page could not be loaded" : "Something went wrong"}
        </h1>
        <p className="small mb-3">
          {chunk
            ? "The app was updated while this tab was open. Reloading will pick up the new version."
            : "You are still signed in, and nothing you had already sent to us is affected."}
        </p>

        {chunk ? (
          <button className="btn btn-sm btn-primary" onClick={() => window.location.reload()}>
            <i className="bi bi-arrow-clockwise me-1" /> Reload
          </button>
        ) : (
          <button className="btn btn-sm btn-primary" onClick={() => this.setState({ error: null })}>
            <i className="bi bi-arrow-clockwise me-1" /> Try again
          </button>
        )}
      </div>
    );
  }
}

/** Route-level: remounted on navigation, so moving to another page clears the error. */
export function RouteErrorBoundary({ children }) {
  const location = useLocation();
  return <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>;
}
